import { baseApi } from "./baseApi";
import Cookies from "js-cookie";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const downloadInsightsExport = async (from?: string, to?: string): Promise<void> => {
  const token = Cookies.get("token") || "";

  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString() ? `?${params.toString()}` : "";

  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/insights/export${qs}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: XLSX_MIME,
      },
    }
  );

  if (!response.ok) throw new Error(`Export failed: ${response.status}`);

  const blob = await response.blob();
  const file = new Blob([blob], { type: XLSX_MIME });
  const url = window.URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fleetrify-insights-${new Date().toISOString().split("T")[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export interface DriverPerformanceEntry {
  driver_id: string;
  driver_name: string;
  branch_name: string;
  total_deliveries: number;
  avg_rating: number | null;
}

export type InsightsSummary = InsightsContext;

export interface InsightsAskRequest {
  question: string;
  from?: string;
  to?: string;
}

export interface InsightsContext {
  period: { from: string | null; to: string | null };
  company_profile?: {
    total_branches: number;
    total_staff: number;
    total_services: number;
  };
  subscription?: {
    plan_name: string;
    billing_cycle: string;
    status: string;
    price: number;
    start_date: string;
    end_date: string;
    days_remaining: number;
    limits: {
      max_users: number;
      max_branches: number;
      max_services: number | null;
    };
  };
  overall: {
    total_orders: number;
    completed_orders: number;
    cancelled_orders: number;
    pending_orders: number;
    total_revenue: number;
    collected_revenue?: number;
    uncollected_revenue?: number;
    completion_rate: number;
    cancellation_rate: number;
  };
  highest_order?: {
    order_id: string;
    order_number: string;
    amount: number;
    status: string;
    payment_status: string;
    date: string;
  };
  profit_loss?: {
    collected_revenue: number;
    uncollected_revenue: number;
    collection_rate: number;
    this_month_revenue: number;
    last_month_revenue: number;
    mom_change_percent: number;
    trend: "up" | "down" | string;
  };
  most_requested: {
    service_id: string;
    service_name: string;
    total_orders: number;
    completed: number;
    cancelled: number;
  }[];
  revenue_by_service: { service_name: string; revenue: number }[];
  top_drivers: {
    driver_id: string;
    driver_name: string;
    completed_jobs: number;
    avg_rating: number | null;
  }[];
  driver_performance?: {
    total_drivers: number;
    total_deliveries_all_drivers: number;
    average_deliveries_per_driver: number;
    most_active_driver: DriverPerformanceEntry | null;
    least_active_driver: DriverPerformanceEntry | null;
    drivers: DriverPerformanceEntry[];
  };
  cancellation_trends: {
    month: string;
    total_orders: number;
    cancellations: number;
    cancellation_rate: number;
  }[];
  monthly_summary: {
    month: string;
    total_orders: number;
    completed: number;
    cancelled: number;
    revenue: number;
    collected_revenue?: number;
    uncollected_revenue?: number;
  }[];
}

export interface InsightsAskResponse {
  status: string;
  data: {
    question: string;
    answer: string;
    context: InsightsContext;
  };
}

export const insightsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInsightsSummary: builder.query<InsightsSummary, void>({
      query: () => ({ url: "/insights/summary", method: "GET" }),
      transformResponse: (res: { status: string; data: InsightsContext }) => res.data,
      providesTags: ["insights"],
    }),

    askInsights: builder.mutation<InsightsAskResponse, InsightsAskRequest>({
      query: (body) => ({
        url: "/insights/ask",
        method: "POST",
        body,
      }),
    }),

  }),
});

export const {
  useGetInsightsSummaryQuery,
  useAskInsightsMutation,
} = insightsApi;
